import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import { useTranslation } from "react-i18next";
import { Box, Button, Card, CardContent, Divider, Grid } from "@mui/material";
import { StyledLink } from "../../styles/componentStyled";
import { SignIn } from "../auth";
import { Link as RouterLink } from "react-router-dom";
import {
  Search,
  TrackChanges,
  Description,
  Business,
} from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectPreference,
  setLanguage,
} from "../../features/preferences/preferencesSlice";
import { useTranslation as useI18n } from "react-i18next";

const TRUST_SIGNALS = [
  {
    icon: <TrackChanges fontSize="small" />,
    label: "Real-time employee status tracking",
  },
  {
    icon: <Description fontSize="small" />,
    label: "Government-compliant report generation",
  },
  {
    icon: <Business fontSize="small" />,
    label: "Multi-agency management in one platform",
  },
];

const LANGS = [
  { code: "en", label: "English", countryCode: "us" },
  { code: "ar", label: "العربية", countryCode: "sa" },
];

function LandingLanguage() {
  const dispatch = useAppDispatch();
  const { i18n } = useI18n();
  const { lang } = useAppSelector(selectPreference);

  const handleLang = (code: string) => {
    i18n.changeLanguage(code);
    dispatch(setLanguage(code));
    document.body.setAttribute("dir", code === "ar" ? "rtl" : "ltr");
  };

  return (
    <Stack direction="row" spacing={1}>
      {LANGS.map((l) => (
        <Button
          key={l.code}
          size="small"
          onClick={() => handleLang(l.code)}
          startIcon={
            <img
              loading="lazy"
              width="18"
              src={`https://flagcdn.com/w40/${l.countryCode}.png`}
              alt={l.label}
              style={{ borderRadius: 2, display: "block" }}
            />
          }
          sx={{
            borderRadius: 999,
            px: 1.5,
            py: 0.5,
            fontSize: "0.8rem",
            fontWeight: 600,
            color: lang === l.code ? "primary.main" : "rgba(255,255,255,0.75)",
            bgcolor:
              lang === l.code
                ? "rgba(255,255,255,0.95)"
                : "rgba(255,255,255,0.10)",
            border: "1px solid",
            borderColor:
              lang === l.code ? "transparent" : "rgba(255,255,255,0.28)",
            "&:hover": {
              bgcolor:
                lang === l.code
                  ? "rgba(255,255,255,1)"
                  : "rgba(255,255,255,0.18)",
            },
          }}
        >
          {l.label}
        </Button>
      ))}
    </Stack>
  );
}

export default function Header() {
  const { t: tt } = useTranslation();
  const { t: ta } = useTranslation("auth");

  return (
    <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
      {/* ── Left column: branding ── */}
      <Grid item xs={12} md={7}>
        <Stack spacing={3} data-aos="fade-right">
          {/* Logo */}
          <Box>
            <img
              src="/cvPlusLogo.png"
              alt="CvPlus"
              style={{ height: 56, objectFit: "contain" }}
            />
          </Box>

          {/* Title + tagline */}
          <Stack spacing={1}>
            {/* <Typography
              variant="h1"
              component="h1"
              sx={{
                color: "common.white",
                fontWeight: 900,
                letterSpacing: "-1.5px",
                lineHeight: 1.05,
                textShadow: "0 2px 20px rgba(0,0,0,0.20)",
              }}
            >
              {tt("platform")}
            </Typography> */}
            <Typography
              variant="h5"
              component="p"
              sx={{
                color: "rgba(255,255,255,0.78)",
                fontWeight: 400,
                letterSpacing: "0.01em",
              }}
            >
              {tt("allInOne")}
            </Typography>
          </Stack>

          {/* Trust signals */}
          <Stack spacing={1.5}>
            {TRUST_SIGNALS.map((signal) => (
              <Stack
                key={signal.label}
                direction="row"
                alignItems="center"
                spacing={1.5}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    bgcolor: "rgba(255,255,255,0.15)",
                    color: "secondary.light",
                    flexShrink: 0,
                  }}
                >
                  {signal.icon}
                </Box>
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.88)",
                    fontWeight: 500,
                    fontSize: "0.95rem",
                  }}
                >
                  {signal.label}
                </Typography>
              </Stack>
            ))}
          </Stack>

          {/* Check Status CTA */}
          <Box>
            <Button
              component={RouterLink}
              to="/check-status"
              variant="contained"
              size="large"
              startIcon={<Search />}
              sx={{
                borderRadius: 999,
                px: 3,
                py: 1.1,
                color: "primary.main",
                bgcolor: "common.white",
                fontWeight: 700,
                fontSize: "0.9rem",
                boxShadow:
                  "0 0 0 2px rgba(255,255,255,0.35), 0 8px 24px rgba(0,0,0,0.18)",
                "&:hover": {
                  bgcolor: "common.white",
                  boxShadow:
                    "0 0 0 3px rgba(255,255,255,0.5), 0 12px 32px rgba(0,0,0,0.24)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              {tt("statusTracking")}
            </Button>
          </Box>

          {/* Language switcher */}
          <LandingLanguage />
        </Stack>
      </Grid>

      {/* ── Right column: sign-in card ── */}
      <Grid item xs={12} md={5} data-aos="fade-left">
        <Card
          elevation={12}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            bgcolor: "background.paper",
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Stack sx={{ mb: 3, alignItems: "center" }}>
              <Typography variant="h5" fontWeight={700} color="primary.main">
                {ta("signin")}
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                Welcome back — sign in to continue
              </Typography>
            </Stack>

            <SignIn embedded />
          </CardContent>

          {/* WhatsApp footer strip */}
          <Box
            sx={{
              borderTop: "1px solid",
              borderColor: "divider",
              py: 1.5,
              display: "flex",
              justifyContent: "center",
              bgcolor: "rgba(0,0,0,0.02)",
            }}
          >
            <StyledLink
              aria-label="Chat on WhatsApp"
              href="https://wa.me/+966556594373"
              target="_blank"
              sx={{
                display: "flex",
                alignItems: "center",
                opacity: 0.85,
                transition: "opacity 0.15s ease",
                "&:hover": { opacity: 1 },
              }}
            >
              <img
                alt="Chat on WhatsApp"
                src="/WhatsAppButtonWhiteLarge.svg"
                width="140px"
                height="36px"
              />
            </StyledLink>
          </Box>
        </Card>

        {/* Subtle copyright */}
        <Typography
          variant="body2"
          sx={{
            color: "rgba(255,255,255,0.45)",
            textAlign: "center",
            mt: 1.5,
            fontSize: "0.75rem",
          }}
        >
          © {new Date().getFullYear()} StellarSofts PLC
        </Typography>
      </Grid>
    </Grid>
  );
}
